import { PropTypes } from "prop-types"
import classNames from "classnames"
import ReactModal from "react-modal"

import CONFIG from "../../environment"

import ModalHeader from "./ModalHeader"
import ModalContent from "./ModalContent"
import ModalFooter from "./ModalFooter"
import { useModalContext } from "./ModalContext"

/**
PropTypes accepted by Modal
a) props from react-modal
  - isOpen: bool (default false)
  - onAfterOpen
  - onRequestClose
  - closeTimeoutMS: number
  - shouldCloseOnOverlayClick: bool to indicate if close modal when click outside (default true)
  - shouldFocusAfterRender (default true)
  - shouldReturnFocusAfterClose (default true)
  - style
  - portalClassName
  - bodyOpenClassName
  - className
  - overlayClassName
  - contentLabel: is the aria-label aka modal title that is read by screen reader (but we also use it as the title, if present)
  - role: accessibility property, in most cases it's dialog
  - aria: any aria attribute you need to pass here (eg: aria-label="Filter" -> aria={{ label: 'filter' }}
  - ariaHideApp
  - appElement
  - parentSelector: function

b) custom props
  - isPrintable. if the modal content should be printed
  - onClose. the function to the triggered on close action. it also derives the shouldCloseOnOverlayClick value
*/

export const Modal = props => {
  const { children, onClose, cssClassName = "", isOpen = false, isPrintable = false } = props
  const { hasChildren, isChild } = useModalContext(isOpen)

  if (!isOpen && isPrintable) {
    return <div className={classNames("only-print", cssClassName)}>{children}</div>
  }
  if (isOpen) {
    return (
      <ReactModal
        className={{
          base: classNames("oj-modal", cssClassName, {
            "has-children": hasChildren,
            "is-child": isChild
          }),
          afterOpen: "open",
          beforeClose: "close"
        }}
        overlayClassName={{
          base: classNames(CONFIG.REACT_APP_ROOT_DIV_CLASS, "modal-overlay", cssClassName, {
            "has-children": hasChildren,
            "is-child": isChild
          }),
          afterOpen: "open",
          beforeClose: "close"
        }}
        portalClassName="ReactModalPortal oj"
        role="dialog"
        appElement={document.getElementById(CONFIG.REACT_APP_ROOT_DIV_ID)}
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={Boolean(onClose)}
        ariaHideApp={false}
        {...props}
      >
        <div className="body-modal">{children}</div>
      </ReactModal>
    )
  }
  return null
}

Modal.propTypes = {
  aria: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node,
  contentLabel: PropTypes.node,
  cssClassName: PropTypes.string,
  isOpen: PropTypes.bool,
  isPrintable: PropTypes.bool,
  onClose: PropTypes.func
}

Modal.Header = ModalHeader
Modal.Content = ModalContent
Modal.Footer = ModalFooter

export default Modal
